import React, { useEffect } from 'react';
import Head from 'next/head';
import setResponseHeaders from '@buzzfeed/headers';
import { useTrackingContext } from '../../hooks/useTrackingContext';
import { TrackingContext } from '../../context/TrackingContext';
import RatRaceNav from '../../components/RatRaceLanding/Nav';
import RatRaceFooter from '../../components/RatRaceLanding/Footer';
import { getRatRacePolicyBasePath } from '../../utils/ratRacePolicyBasePath';
import privacyPolicyImage from '../../public/static/images/rat-race/privacy-policy.png';
import styles from './privacy-policy.module.scss';

const PAGE_INFO = {
  context_page_type: 'utility',
  context_page_id: 'rat-race/privacy-policy',
  destination: 'buzzfeed',
  page_edition: 'en-us',
};

const TIME_SPENT_INFO = {
  ...PAGE_INFO,
  unit_type: 'component',
  unit_name: 'rat_race_privacy_policy',
};

const getAssetSrc = (asset) => asset?.src || asset;

function RatRacePrivacyPolicyContent({ policyBasePath }) {
  const { trackPageview, attachTimeSpentHandler } = useTrackingContext();

  useEffect(() => {
    trackPageview(PAGE_INFO);
    attachTimeSpentHandler(TIME_SPENT_INFO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <title>Rat Race | Privacy Policy</title>
        <meta
          name="description"
          content="RAT RACE's Privacy Policy"
        />
      </Head>

      <div className={styles.page}>
        <RatRaceNav />

        <main className={styles.privacyPolicy}>
          <header className={styles.header}>
            <h1>RAT RACE&apos;s Privacy Policy</h1>
            <p className={styles.effectiveDate}>Effective August 10, 2026</p>
            <img
              src={getAssetSrc(privacyPolicyImage)}
              alt="Rat Race Privacy Policy"
              className={styles.heroImage}
            />
          </header>

          <div className={styles.intro}>
            <p>
              Welcome to RAT RACE&apos;s Privacy Policy, which is a part of RAT
              RACE&apos;s User Agreement. This Privacy Policy explains how RAT
              RACE collects, uses, and discloses personal data (as defined
              herein) related to your use. When we mention &quot;BuzzFeed&quot;,
              &quot;we&quot;, &quot;us&quot; or &quot;our&quot; in this Privacy
              Policy, we mean BuzzFeed, Inc., or the relevant BuzzFeed company
              or business division responsible for processing your data.
            </p>
            <p>
              United States users can find out about your privacy rights in
              the{' '}
              <a href="#section-11">
                Privacy Information for United States Residents
              </a>{' '}
              section.
            </p>
            <p>
              EU/UK users can find out about your rights in the{' '}
              <a href="#section-10">
                Privacy Information for EEA and UK Users
              </a>{' '}
              section. For all other users, please see{' '}
              <a href="#section-12">
                Privacy Information for All Users, Regardless of Location
              </a>
              .
            </p>
          </div>

          <nav className={styles.toc} aria-label="Table of contents">
            <a href="#section-1">1. About this Privacy Policy</a>
            <a href="#section-2">2. Changes to this Privacy Policy</a>
            <a href="#section-3">3. How to Contact Us</a>
            <a href="#section-4">4. What Personal Data we Collect and How</a>
            <a href="#section-5">5. How We Use Your Personal Data</a>
            <a href="#section-6">
              6. Disclosures; Sharing or Selling Your Personal Data
            </a>
            <a href="#section-7">7. Aggregated Data</a>
            <a href="#section-8">
              8. International Transfer of your Personal Data
            </a>
            <a href="#section-9">9. Retaining Your Personal Data</a>
            <a href="#section-10">
              10. Privacy Information for EEA and UK Users
            </a>
            <a href="#section-11">
              11. Privacy Information for United States Residents
            </a>
            <a href="#section-12">
              12. Privacy Information for All Users, Regardless of Location
            </a>
          </nav>

          <div className={styles.sections}>
            <section id="section-1" className={styles.policySection}>
              <h2>1. About this Privacy Policy</h2>
              <p>
                This Privacy Policy explains how RAT RACE collects, uses, and
                discloses your personal data. We collect personal data when
                you use the RAT RACE&trade; mobile application, its associated
                website, and the services, features, and content available on
                the application or website (collectively, &quot;RAT
                RACE&quot;).
              </p>
            </section>

            <section id="section-2" className={styles.policySection}>
              <h2>2. Changes to this Privacy Policy</h2>
              <p>
                We&apos;re likely to update this Privacy Policy in the future
                and when we make changes, we will revise the date at the top
                of the policy. When we make significant changes, we will
                notify you through the mobile application of RAT RACE, via
                email or other communication.
              </p>
            </section>

            <section id="section-3" className={styles.policySection}>
              <h2>3. How To Contact Us</h2>
              <p className={styles.contactIntro}>
                If you have questions or concerns about the information in
                this Privacy Policy, our handling of your personal data more
                generally, your choices and rights regarding such use, or
                wish to exercise your rights under law, please do not
                hesitate to contact us at:
              </p>
              <address className={styles.address}>
                BuzzFeed, Inc.
                <br />
                50 West 23rd Street, 6th Floor
                <br />
                New York, NY 10010
                <br />
                Attn: Legal Department
                <br />
                <a href="mailto:data-privacy@buzzfeed.com">
                  data-privacy@buzzfeed.com
                </a>
              </address>
            </section>

            <section id="section-4" className={styles.policySection}>
              <h2>4. What Personal Data We Collect And How</h2>

              <div className={styles.subsection}>
                <h3>
                  4.1. Personal Data You Provide To Us, Including Health Data.
                </h3>
                <p>We collect data you provide directly to us.</p>
                <ul>
                  <li>
                    <strong>Account data.</strong> When you create an account
                    with us (&quot;Account&quot;), we will collect your Google
                    or Apple accounts to enable third party authentication. We
                    will also collect and maintain your username.
                  </li>
                  <li>
                    <strong>Payment data.</strong> To order products or
                    services through RAT RACE, we will collect your payment
                    information through your payment processor. Please note
                    that your financial information is collected and stored
                    by a third party payment processing company. Use and
                    storage of that data is governed by the third party
                    payment processor&apos;s applicable privacy policy.
                  </li>
                  <li>
                    <strong>Purchase data.</strong> If you make a purchase on
                    RAT RACE, we will receive data about the purchase.
                  </li>
                  <li>
                    <strong>App Restriction Information.</strong> To provide
                    app-blocking functionality, we will collect a list of
                    applications you choose to restrict, your configured
                    goals, and device authorization status necessary to
                    enforce restrictions. We do not collect the content of
                    other applications or monitor your activity with those
                    applications.
                  </li>
                  <li>
                    <strong>Other data you share through RAT RACE.</strong>{' '}
                    You may provide us with data when you upload a picture of
                    yourself, update or add information to your Account,
                    respond to surveys, post to forums, provide us with
                    feedback, participate in promotions, or use other
                    features of RAT RACE.
                  </li>
                </ul>

                <p>
                  With your permission, Rat Race may access health and
                  activity information from your device or authorized
                  third-party health platform (&quot;Health Data&quot;),
                  including:
                </p>
                <ul className={styles.healthDataList}>
                  <li>Daily step count</li>
                  <li>Walking distance</li>
                  <li>Activity history</li>
                  <li>Activity timestamps</li>
                  <li>Goal progress</li>
                  <li>Device motion information</li>
                </ul>

                <p>
                  We access only the information you authorize. We do not
                  collect medical records, diagnoses, prescriptions, or
                  healthcare provider information.
                </p>
                <p>
                  When we conduct research or carry out surveys and you
                  participate in our research or surveys, we may collect
                  sensitive personal data or sensitive personal information
                  (&quot;SPDI&quot;) about you, for example, information
                  about your racial or ethnic origin, political opinions,
                  religious or philosophical beliefs, sex life or sexual
                  orientation. We will get your explicit consent for our
                  collection, use, or sharing of this data as required by
                  law. We do not collect or process SPDI for any other
                  purpose other than those permitted by applicable law.
                </p>
              </div>

              <div className={styles.subsection}>
                <h3>
                  4.2. Data That We Collect Automatically About Your Browser
                  And Device.
                </h3>
                <p>
                  When you access or use RAT RACE, we use cookies and similar
                  technologies that collect information about your browser
                  and device:
                </p>
                <ul>
                  <li>
                    <strong>Log Data.</strong> We automatically receive and
                    record information from your device or web browser when
                    you interact with RAT RACE. For example, we may collect
                    your device model, app model, device identifier, internet
                    protocol (IP), time zone setting, language preference,
                    operating system, platform, and internet service provider
                    (ISP).
                  </li>
                  <li>
                    <strong>Account ID.</strong> If you create an Account ID,
                    we will use this information to collect data about your
                    activities through RAT RACE.
                  </li>
                  <li>
                    <strong>Usage and Analytics Information.</strong> We
                    automatically collect information regarding your use of
                    RAT RACE including, features used, session duration, goal
                    completion statistics, notification interactions, screens
                    or pages viewed, buttons clicks, app launches and exits,
                    crash and performance information, and other diagnostic
                    and usage information.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3>4.3. Data That We Collect From Third-Parties.</h3>
                <p>
                  RAT RACE uses information provided by your mobile device and
                  may request permission to access the following information:
                </p>
                <ul className={styles.healthDataList}>
                  <li>Motion and fitness activity</li>
                  <li>HealthKit</li>
                  <li>Health Connect and/or</li>
                  <li>device administration permissions.</li>
                </ul>
                <p>
                  You may revoke permissions at any time through your device
                  settings. However, revoking your permissions may disable
                  certain functionalities.
                </p>
              </div>
            </section>

            <section id="section-5" className={styles.policySection}>
              <h2>5. How We Use Your Personal Data</h2>
              <p className={styles.dataUseText}>
                We use the data we collect to provide, maintain, and improve
                RAT RACE. Specifically, we use your data to enable creation of
                your account, maintain your account and statistics, track
                your activity goals, display your statistics, and restrict
                selected applications according to your settings.
              </p>
              <p className={styles.dataUseText}>
                We also use the data we collect to:
              </p>
              <ul className={styles.dataUseText}>
                <li>
                  Send you technical notices, updates, security alerts and
                  support and administrative messages and to respond to your
                  comments, questions and customer service requests;
                </li>
                <li>
                  Communicate with you about products, services, offers, and
                  events offered by RAT RACE and others, and provide news and
                  information we think will be of interest to you;
                </li>
                <li>
                  Monitor and analyze trends, usage and activities in
                  connection with RAT RACE and personalize and improve RAT
                  RACE;
                </li>
                <li>
                  Detect, investigate and prevent fraudulent transactions and
                  other illegal activities and protect the rights and
                  property of BuzzFeed and others; and
                </li>
                <li>
                  Carry out any other purpose described to you at the time
                  the data was collected.
                </li>
              </ul>
              <p className={styles.dataUseText}>
                Your Health Data is processed only as necessary to restrict
                selected applications according to settings, monitor and
                track your health, and display your results. We do not use
                your Health Data to make automated decisions that produce
                legal or similarly significant effects.
              </p>
            </section>

            <section id="section-6" className={styles.policySection}>
              <h2>6. Disclosures; Sharing Or Selling Your Personal Data</h2>
              <p className={styles.disclosureIntro}>
                In general, your personal data will remain with BuzzFeed but
                we may disclose personal data about you as follows or as
                otherwise described in this Privacy Policy:
              </p>
              <ul className={styles.disclosureList}>
                <li>
                  Within BuzzFeed. We share your personal data within
                  BuzzFeed&apos;s legal entities where necessary to fulfill a
                  contract or precontractual measures, or where we have a
                  legitimate interest in doing so or a legal obligation to do
                  so.
                </li>
                <li>
                  With vendors, consultants, and other service providers who
                  process your data on our behalf when they provide services
                  to us, for example cloud hosting, authentication, payment
                  processing, analytics, crash reporting, and fraud
                  prevention.
                </li>
                <li>
                  In connection with, or during negotiations of, any merger,
                  sale of company assets, financing or acquisition of all or
                  a portion of our business by another company;
                </li>
                <li>
                  We may be legally required to disclose or share your
                  personal data without your consent in some circumstances,
                  for example to comply with a court order or law
                  enforcement. In such circumstances, we will only disclose
                  your personal data if we have a good-faith belief that such
                  sharing is required under applicable legal obligations.
                </li>
              </ul>
              <p className={styles.disclosureIntro}>
                We do not sell or share your personal data, including your
                health data, for advertising.
              </p>
            </section>

            <section id="section-7" className={styles.policySection}>
              <h2>7. Aggregated Data</h2>
              <p className={styles.dataUseText}>
                We analyze and aggregate personal data we collect, such as the
                number and frequency of your visits to the Services, to
                produce anonymized metrics and statistical information that
                does not identify you as an individual.
              </p>
              <p className={styles.dataUseText}>
                We combine information about you with information about
                other users to create audience segments of aggregated data,
                for example, statistical, demographic and usage data. If we
                combine or connect aggregated data with your personal data,
                we treat the combined data as personal data and treat it in
                accordance with this Privacy Policy.
              </p>
            </section>

            <section id="section-8" className={styles.policySection}>
              <h2>8. International Transfer Of Your Personal Data</h2>
              <p>
                RAT RACE is operated from the United States. If you access RAT
                RACE from outside the United States, you acknowledge that
                your personal data may be transferred to, stored, and
                processed in the United States and other countries where we
                or our service providers maintain facilities.
              </p>
              <p>
                These countries may have data protection laws that differ
                from those in your jurisdiction and may not provide the same
                level of protection as the laws of your country.
              </p>
              <p>
                Where required by applicable law, we implement appropriate
                safeguards for international transfers of personal data,
                including the use of the European Commission&apos;s Standard
                Contractual Clauses (&quot;SCCs&quot;) or other legally
                recognized transfer mechanisms.
              </p>
              <p>
                By using RAT RACE or otherwise providing us with your
                information, you acknowledge that your personal data may be
                transferred as described in this Privacy Policy.
              </p>
            </section>

            <section id="section-9" className={styles.policySection}>
              <h2>9. Retaining Your Personal Data</h2>
              <p>
                We keep your personal data for as long as we need it for the
                purposes for which it was collected.
              </p>
              <p>
                When we decide how long we will keep your information we
                consider the amount, nature, and sensitivity of the personal
                data, the potential risk of harm from unauthorized use or
                disclosure, why we need it, and any relevant legal
                requirements (such as legal retention and destruction
                periods).
              </p>
              <p>
                We may anonymize your personal data (so that it can no longer
                be associated with you) for research or statistical purposes.
              </p>
            </section>

            <section id="section-10" className={styles.policySection}>
              <h2>10. Privacy Information For EEA And UK Users</h2>

              <div className={styles.subsection}>
                <h3>10.1. Information For EU Users (Including Legal Bases)</h3>
                <p>
                  We will use your personal data only for the purposes for
                  which we collect it, unless we reasonably consider that we
                  need to use it for another reason that is compatible with
                  the original purpose. If we need to use your personal data
                  for another purpose, we will explain the legal basis we
                  rely on.
                </p>
                <p>
                  Depending on the purpose, we process your personal data
                  according to the following basis:
                </p>
                <ul className={styles.legalBasisList}>
                  <li>Your consent,</li>
                  <li>Performance of a contract,</li>
                  <li>Our legitimate interest,</li>
                  <li>Compliance with legal obligations</li>
                </ul>
                <p>
                  The table below shows what we use your personal data for
                  and the lawful basis for processing it in each case.
                </p>
                <div className={styles.tableWrapper}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Purpose</th>
                        <th>Type of Data</th>
                        <th>Lawful Basis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td data-label="Purpose">Creating and maintaining your account</td>
                        <td data-label="Type of Data">Account Data</td>
                        <td data-label="Lawful Basis">Performance of Contract</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Measuring your steps</td>
                        <td data-label="Type of Data">Health Data</td>
                        <td data-label="Lawful Basis">Your Consent</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Enforcing app restriction</td>
                        <td data-label="Type of Data">App Restriction Information</td>
                        <td data-label="Lawful Basis">Your Consent, Performance of Contract</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Accessing HealthKit or Health Connect data</td>
                        <td data-label="Type of Data">Health Data</td>
                        <td data-label="Lawful Basis">Your Consent</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Fraud Prevention and security</td>
                        <td data-label="Type of Data">Account Data, Health Data, Log Data</td>
                        <td data-label="Lawful Basis">Legitimate Interest</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Compliance with Applicable Laws</td>
                        <td data-label="Type of Data">Any (as requested)</td>
                        <td data-label="Lawful Basis">Legal Obligation</td>
                      </tr>
                      <tr>
                        <td data-label="Purpose">Product analytics and improvement</td>
                        <td data-label="Type of Data">Any (as needed)</td>
                        <td data-label="Lawful Basis">Legitimate Interest</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.subsection}>
                <h3>10.2. Legal Rights For EEA Users</h3>
                <p>
                  If you are an EU resident, you have certain rights in
                  respect of the information that we hold about you. Below
                  is a short overview of those rights:
                </p>
                <ul>
                  <li>
                    <strong>Access.</strong> With some exceptions designed to
                    protect the rights of others, you have the right to a
                    copy of the personal data that we hold about you. Where
                    the data is data that you have given to us, you have the
                    right to receive your copy of it in a common electronic
                    format, and to provide copies of it to other people if
                    you wish.
                  </li>
                  <li>
                    <strong>Correction.</strong> You have the right to have
                    the personal data we hold about you corrected if it is
                    factually inaccurate. This right does not extend to
                    matters of opinion, such as views expressed in editorial
                    content.
                  </li>
                  <li>
                    <strong>Deletion.</strong> In some limited circumstances,
                    you have the right to have personal data that we hold
                    about you erased (the &quot;right to be forgotten&quot;).
                    This right is not generally available where we still
                    have a valid legal reason to keep the data (for example,
                    because we are obliged to do so by law).
                  </li>
                  <li>
                    <strong>Objection.</strong> You have the right to object
                    to our processing of your personal data where we rely on
                    &quot;legitimate interests&quot; as our legal basis for
                    processing, but we may be able to continue processing if
                    our interest outweighs your objection.
                  </li>
                  <li>
                    <strong>Withdrawing consent.</strong> If we are
                    processing your personal data on the basis of your
                    consent, you have the right to withdraw that consent at
                    any time, in which case we will stop that processing
                    unless we have another legal basis on which to continue.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3>10.3. How To Exercise Your Rights</h3>
                <p>
                  To exercise your rights, please go to the settings in RAT
                  RACE or contact us at{' '}
                  <a href="mailto:data-privacy@buzzfeed.com">
                    data-privacy@buzzfeed.com
                  </a>
                  . In order to protect your privacy and that of others, we
                  will ask you to prove your identity before we take any
                  steps in response to a request you have made. If a third
                  party is making a request on your behalf, we will ask them
                  to prove that they have your permission to act for you.
                </p>
                <p>
                  We may contact you to ask you for further information to
                  help you exercise your legal rights or to speed up our
                  response. We will try to respond to requests within one
                  month and we will let you know if we need to take longer,
                  for example because your request is complicated. We may
                  have an overriding lawful basis for processing your
                  personal data, or certain exemptions may apply. If we
                  refuse your request, we will explain why. You may also
                  reach us at{' '}
                  <a href="mailto:data-privacy@buzzfeed.com">
                    data-privacy@buzzfeed.com
                  </a>{' '}
                  to exercise your data privacy rights described above,
                  including the right to deletion. You may also contact our
                  data protection office (DPO) or our EU Representative.
                </p>
                <p>
                  Our Data Protection Officer (DPO) is DP Dock DPO Services
                  GmbH. Their contact details are:
                </p>
                <address className={styles.contactBlock}>
                  DP Dock DPO Services GmbH, Gr&uuml;ffkamp 10, 24159 Kiel,
                  Germany
                  <br />
                  +49 (0) 1515 7868665
                  <br />
                  <a href="mailto:buzzfeed@dp-officer.com">
                    buzzfeed@dp-officer.com
                  </a>
                </address>
                <p>
                  Our EU Representative is EU-REP.Global GmbH. Their contact
                  details are:
                </p>
                <address className={styles.contactBlock}>
                  EU-REP.Global GmbH, Attn: BuzzFeed, Inc., Hopfenstra&szlig;e
                  1d, 24114 Kiel, Germany
                  <br />
                  <a href="mailto:buzzfeed@eu-rep.global">
                    buzzfeed@eu-rep.global
                  </a>
                </address>
              </div>
            </section>

            <section id="section-11" className={styles.policySection}>
              <h2>11. Privacy Information for United States Residents</h2>
              <p>
                Residents of certain states have additional rights and
                choices. This section addresses those additional rights.
              </p>

              <div className={styles.subsection}>
                <h3>11.1. Privacy Rights</h3>
                <ul className={styles.privacyRightsList}>
                  <li>
                    Right to Deletion. You have the right to request that we
                    delete certain personal data we have about you.
                  </li>
                  <li>
                    Right to Access. You have the right to request that we
                    inform you about the categories of personal data we
                    collect or disclose about you; the categories of sources
                    of such data; the business or commercial purpose for
                    collecting your personal data; and the categories of
                    third parties with whom we share/disclose personal data.
                    Such information is also set forth above in this Privacy
                    Policy. You may also request that we provide you access
                    to and/or a copy of the specific pieces of personal data
                    we collected about you (also called a data portability
                    request).
                  </li>
                  <li>
                    Right to Correction. If you feel that any of the records
                    of personal data we have about you are incorrect, you
                    have a right to request that we correct those
                    inaccuracies. This right does not extend to matters of
                    opinion, such as views expressed in editorial content.
                  </li>
                  <li>
                    No Discrimination. You have the right to not be
                    discriminated against (as provided for in the applicable
                    US Privacy Law) for exercising your rights.
                  </li>
                  <li>
                    Right to Opt-Out. You have the right to opt-out of the
                    &quot;sale&quot; of your personal data, or the
                    &quot;sharing&quot; of your personal data for
                    interest-based or other targeted advertising on third
                    party websites. Note that &quot;sale&quot; is defined
                    broadly and may include allowing third parties to
                    receive certain data, such as cookies, IP address,
                    and/or browsing behavior, to deliver targeted
                    advertising on our Services or other sites and apps. We
                    do not engage in the sale/sharing of your personal data
                    for targeted advertising.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3>11.2. Notice for California Users</h3>
                <p>
                  This section provides you with our practices regarding the
                  collection, use, and disclosure of your personal data in
                  the 12 months prior to our last update to this Policy (see
                  the date at the top). It also explains your rights
                  regarding your personal data that we collect pursuant to
                  applicable U.S. state privacy laws.
                </p>
                <p>
                  <strong>Categories of Information We Collect:</strong>
                </p>
                <ul>
                  <li>
                    Your contact details (e.g. your user name; your Apple or
                    Google sign-in information).
                  </li>
                  <li>
                    Purchase history &amp; preferences (e.g. records of
                    services purchased or obtained).
                  </li>
                  <li>
                    Internet activity information (e.g., browsing history,
                    search history, information on a consumer&apos;s
                    interaction with a website, application, or questions
                    and other messages you address to us directly through
                    online forms or by email).
                  </li>
                  <li>
                    Online identifiers (e.g. IP address, ADID/IDFA, internal
                    identification numbers).
                  </li>
                </ul>
                <p>
                  <strong>Categories of Sources:</strong>
                </p>
                <ul>
                  <li>You</li>
                  <li>Automatically from our device</li>
                  <li>
                    From Apple, Google, or other third-party services that
                    you authorize
                  </li>
                </ul>
                <p>
                  <strong>Purpose of Collection</strong>
                </p>
                <ul>
                  <li>
                    To provide and maintain RAT RACE, including the ability
                    to track your health data and provide restriction
                    functionality on your selected apps.
                  </li>
                  <li>To register and authenticate your Account.</li>
                  <li>
                    To communicate with you, including to process your
                    requests for products/services and/or information.
                  </li>
                  <li>
                    To improve RAT RACE, including to better understand our
                    customer base.
                  </li>
                  <li>
                    To enforce our User Agreement, and for the
                    administration and protection of our business.
                  </li>
                  <li>
                    To comply with applicable law, regulations, and legal and
                    regulatory processes.
                  </li>
                </ul>
                <p>
                  <strong>Disclosures.</strong> We do not sell or share your
                  personal data for targeted advertising. We may disclose
                  your personal data, including health data, with our
                  service providers and business partners only as needed to
                  provide RAT RACE. We provide details of our disclosure
                  below:
                </p>
                <div className={styles.tableWrapper}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Categories of Data Disclosed</th>
                        <th>
                          Categories of Vendors or Other Entities We Disclose
                          Data To
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td data-label="Categories of Data Disclosed">
                          Your contact details
                        </td>
                        <td data-label="Categories of Vendors or Other Entities We Disclose Data To">
                          Our authorization partners and service vendors who
                          assist in providing services on our behalf.
                        </td>
                      </tr>
                      <tr>
                        <td data-label="Categories of Data Disclosed">
                          Your health data.
                        </td>
                        <td data-label="Categories of Vendors or Other Entities We Disclose Data To">
                          Our service providers who assist in providing RAT
                          RACE.
                        </td>
                      </tr>
                      <tr>
                        <td data-label="Categories of Data Disclosed">
                          Payment information
                        </td>
                        <td data-label="Categories of Vendors or Other Entities We Disclose Data To">
                          Our payment processing vendors.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  We may share your personal data with government entities
                  when required to do so by law.
                </p>
                <p>
                  <strong>Your privacy rights.</strong> As a California
                  resident, the following rights are available to you. You
                  may exercise these rights by going to your settings or
                  emailing us at{' '}
                  <a href="mailto:data-privacy@buzzfeed.com">
                    data-privacy@buzzfeed.com
                  </a>
                  .
                </p>
                <ul>
                  <li>
                    Right to Know the categories of specific personal data we
                    maintain about you.
                  </li>
                  <li>
                    Right to Correct inaccurate personal data we maintain
                    about you.
                  </li>
                  <li>Right to Delete your personal data;</li>
                  <li>
                    Right to Obtain a Portable Copy of the personal data we
                    hold about you;
                  </li>
                  <li>
                    Right to Limit the Use and Disclosure of sensitive
                    information.
                  </li>
                  <li>Right to Appeal the Denial of Your Request.</li>
                  <li>
                    Right to be Free from Discrimination for exercising your
                    privacy rights.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3>11.3. California Shine the Light Disclosure</h3>
                <p>
                  Under California Civil Code sections 1798.83-1798.84,
                  California residents are entitled to ask for a notice
                  identifying the categories of personal customer data which
                  we share with our affiliates and/or third parties for their
                  own direct marketing purposes, and providing contact
                  information for such affiliates and/or third parties. RAT
                  RACE does not share any personal data for direct marketing
                  purposes.
                </p>
              </div>
            </section>

            <section id="section-12" className={styles.policySection}>
              <h2>
                12. Privacy Information for All Users, Regardless of Location
              </h2>
              <p>
                As described above, depending on your place of residence, you
                may have certain data privacy rights afforded by law; however,
                we allow you to exercise select privacy rights regardless of
                your jurisdiction.
              </p>
              <p>
                <strong>Deletion and Access</strong>
              </p>
              <p>
                If you would like to have us delete your personal data or
                provide you with access to the personal data we have about
                you, you can make the request by going to settings or email
                us at{' '}
                <a href="mailto:data-privacy@buzzfeed.com">
                  data-privacy@buzzfeed.com
                </a>
                . We may request additional information to verify your
                identity before processing your request. Authorized agents
                may submit requests on your behalf in accordance with
                applicable law.
              </p>
              <p>
                <strong>Opt-out of Sale/Share/Targeted Advertising.</strong>
              </p>
              <p>
                We do not sell or share your personal data for targeted
                advertising.
              </p>
              <p>
                <strong>Disclaimer and Appeal.</strong>
              </p>
              <p>
                Please note that certain data may be exempt from such
                access, data portability, correction, and deletion requests
                under US Privacy Law. For example, we need to retain certain
                data in order to provide RAT RACE to you or to comply with
                our legal obligations.
              </p>
              <p>
                If we are unable to honor your request or you are not
                satisfied with the outcome of your request, you have the
                right, within a reasonable period of time, to appeal our
                decision. Please send your appeal by email to{' '}
                <a href="mailto:data-privacy@buzzfeed.com">
                  data-privacy@buzzfeed.com
                </a>{' '}
                with &quot;Privacy Appeal&quot; in the subject line.
              </p>
            </section>
          </div>
        </main>

        <RatRaceFooter policyBasePath={policyBasePath} />
      </div>
    </>
  );
}

export async function getServerSideProps({ req, res }) {
  setResponseHeaders({
    req,
    res,
    maxAge: 86400,
    contentIDs: [{ type: 'quiz', id: 'rat-race-privacy-policy' }],
  });

  const policyBasePath = getRatRacePolicyBasePath(req);

  return { props: { policyBasePath } };
}

export default function RatRacePrivacyPolicyPage({ policyBasePath }) {
  return (
    <TrackingContext.Provider value={PAGE_INFO}>
      <RatRacePrivacyPolicyContent policyBasePath={policyBasePath} />
    </TrackingContext.Provider>
  );
}
